import { getBrowserClient } from "./supabase-browser.js";

/**
 * Shop data access for the browser. Replaces src/lib/shopify.ts — instead of
 * fetching products from Shopify's Storefront API, we read them from our own
 * Supabase catalog (tables `products`, `product_variants`, `product_images`).
 *
 * Checkout still goes through a serverless endpoint (`/api/kustom-checkout`)
 * because we need the secret API key to talk to Kustom.
 */

export interface ProductVariant {
  id: string;
  sku: string | null;
  title: string;
  priceOre: number;
  taxRateBp: number;
  inventory: number | null;
  sortOrder: number;
}

export interface ProductImage {
  id: string;
  url: string;
  alt: string | null;
  sortOrder: number;
}

export type ProductCategory = "mennesker" | "hunder";

export interface Product {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  descriptionHtml: string | null;
  sortOrder: number;
  category: ProductCategory;
  variants: ProductVariant[];
  images: ProductImage[];
}

function publicImageUrl(storagePath: string): string {
  // During the Shopify → Supabase migration we keep the Shopify CDN URLs in
  // `storage_path` temporarily. Any absolute http(s) URL passes through; only
  // relative paths get resolved against the Supabase bucket.
  if (/^https?:\/\//i.test(storagePath)) return storagePath;
  const url = import.meta.env.VITE_SUPABASE_URL?.trim();
  if (!url) return storagePath;
  return `${url.replace(/\/$/, "")}/storage/v1/object/public/product-images/${storagePath.replace(/^\//, "")}`;
}

export async function listActiveProducts(options?: {
  category?: ProductCategory;
}): Promise<Product[]> {
  const supabase = getBrowserClient();
  let query = supabase
    .from("products")
    .select(
      `
        id, slug, title, description, description_html, sort_order, category,
        product_variants(id, sku, title, price_ore, tax_rate_bp, inventory, sort_order, is_active),
        product_images(id, storage_path, alt, sort_order)
      `
    )
    .eq("is_active", true)
    .order("sort_order", { ascending: true });

  if (options?.category) {
    query = query.eq("category", options.category);
  }

  const { data, error } = await query;
  if (error) throw error;

  return (data ?? []).map((row) => toProduct(row));
}

export async function getProductBySlug(slug: string): Promise<Product | null> {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from("products")
    .select(
      `
        id, slug, title, description, description_html, sort_order, category,
        product_variants(id, sku, title, price_ore, tax_rate_bp, inventory, sort_order, is_active),
        product_images(id, storage_path, alt, sort_order)
      `
    )
    .eq("slug", slug)
    .eq("is_active", true)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return toProduct(data);
}

type DbProductRow = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  description_html: string | null;
  sort_order: number;
  category: string;
  product_variants: Array<{
    id: string;
    sku: string | null;
    title: string;
    price_ore: number;
    tax_rate_bp: number;
    inventory: number | null;
    sort_order: number;
    is_active: boolean;
  }>;
  product_images: Array<{
    id: string;
    storage_path: string;
    alt: string | null;
    sort_order: number;
  }>;
};

function toProduct(row: DbProductRow): Product {
  const category: ProductCategory =
    row.category === "hunder" ? "hunder" : "mennesker";
  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    description: row.description,
    descriptionHtml: row.description_html,
    sortOrder: row.sort_order,
    category,
    variants: (row.product_variants ?? [])
      .filter((v) => v.is_active)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((v) => ({
        id: v.id,
        sku: v.sku,
        title: v.title,
        priceOre: v.price_ore,
        taxRateBp: v.tax_rate_bp,
        inventory: v.inventory,
        sortOrder: v.sort_order,
      })),
    images: (row.product_images ?? [])
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((img) => ({
        id: img.id,
        url: publicImageUrl(img.storage_path),
        alt: img.alt,
        sortOrder: img.sort_order,
      })),
  };
}

export function formatPriceNok(priceOre: number): string {
  const kr = priceOre / 100;
  return `${kr.toLocaleString("nb-NO", { maximumFractionDigits: 2 })} kr`;
}
